import React, {forwardRef} from 'react';
import {Link as RouterLink} from 'react-router-dom';
import {useInitialLocation} from './context';
import {createRouteState} from './useHistory';

export type RouterLinkTarget = Parameters<typeof RouterLink>[0]['to'];

interface Props {
  openExternalLinkInNewTab?: boolean;
  'data-testid'?: string;
  considerScrollTopAnchor?: boolean;
  scrollTopAnchorId?: string;
  forcePushBehaviour?: boolean;
}

export type LinkProps = Parameters<RouterLink>[0] & Props;

export const Link = forwardRef<HTMLAnchorElement, LinkProps>(function Link(
  {
    to: originalTo,
    openExternalLinkInNewTab = true,
    'data-testid': dataTestId = 'link',
    considerScrollTopAnchor,
    scrollTopAnchorId,
    forcePushBehaviour,
    ...props
  },
  forwardedRef,
) {
  const unifiedTo = useUnifyLinkUrl(originalTo);
  const isExternalUrl =
    typeof unifiedTo === 'string' &&
    (isSpecial(unifiedTo) || isExternal(unifiedTo));
  // on the server, if the url is a string, we don't care about state or click handlers
  // this is a pure cost reduction on the server
  if (!process.browser && process.env.NODE_ENV !== 'test') {
    // most urls are strings, therefore we only optimise for those
    if (typeof unifiedTo === 'string') {
      const {component: Component = 'a', ...serverProps} = props;
      if (isExternalUrl) {
        serverProps.target = openExternalLinkInNewTab ? '_blank' : '_self';
        serverProps.rel = 'noreferrer';
      }

      return (
        <Component
          ref={forwardedRef}
          href={unifiedTo}
          data-testid={dataTestId}
          {...serverProps}
        />
      );
    }
  }

  const state = createRouteState({
    considerScrollTopAnchor,
    scrollTopAnchorId,
    forcePushBehaviour,
  });

  // eslint-disable-next-line react-hooks/rules-of-hooks
  const to = useGetTo(unifiedTo, state);

  if (isExternalUrl) {
    return (
      <a
        ref={forwardedRef}
        href={unifiedTo}
        data-testid={dataTestId}
        {...props}
        target={openExternalLinkInNewTab ? '_blank' : '_self'}
        rel="noreferrer"
      />
    );
  }

  return (
    <RouterLink
      ref={forwardedRef}
      to={to}
      data-testid={dataTestId}
      {...props}
    />
  );
});

const useHostname = () => {
  const {host, protocol} = useInitialLocation();
  return `${protocol}://${host}`;
};

const useUnifyLinkUrl = (to: Parameters<RouterLink>[0]['to']) => {
  const hostname = useHostname();
  if (typeof to !== 'string') {
    return to;
  }

  let nextTo = to;
  if (nextTo.startsWith(hostname)) {
    nextTo = nextTo.replace(hostname, '');
  }
  if (nextTo.startsWith('?')) {
    nextTo = `/${nextTo}`;
  }
  return nextTo;
};

const useGetTo = (
  originalTo: Parameters<RouterLink>[0]['to'],
  state?: object,
) => {
  const hostname = useHostname();
  if (!state) {
    return originalTo;
  }
  switch (typeof originalTo) {
    case 'function':
      return originalTo;
    case 'string': {
      const url = new URL(originalTo, hostname);
      return {
        pathname: url.pathname,
        search: url.search,
        hash: url.hash,
        state,
      };
    }
    default:
      return {
        ...originalTo,
        state: Object.assign(state, originalTo.state),
      };
  }
};

const specialPrefix = ['mailto:', 'tel:', 'appaboutyou:', 'intent:'];
export function isSpecial(href: string) {
  return (
    href[0] !== '/' && specialPrefix.some(prefix => href.startsWith(prefix))
  );
}

const externalPrefix = ['https:', 'http:', 'www.'];
export function isExternal(href: string) {
  return (
    href[0] !== '/' && externalPrefix.some(prefix => href.startsWith(prefix))
  );
}
